import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Image,
  VStack,
  HStack,
  Button,
  Badge,
  Flex,
} from "@chakra-ui/react";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const BlogCard = ({ id, slug, title, excerpt, image, category, date, readTime, index = 0, useAnimation = false }) => {
  const navigate = useNavigate();
  const bgColor = "brand.bg.card";
  const textColor = "brand.text.primary";
  const descriptionColor = "brand.text.secondary";

  const handleCardClick = () => {
    navigate(`/blog/${slug || id}`);
  };

  const handleButtonClick = (e) => {
    e.stopPropagation(); // Prevent card click from firing
    navigate(`/blog/${slug || id}`);
  };

  // Use regular Box if animations are disabled
  if (!useAnimation) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        overflow="hidden"
        boxShadow="lg"
        backdropFilter="blur(10px)"
        transition="transform 0.3s, box-shadow 0.3s"
        _hover={{ 
          transform: "translateY(-5px)", 
          boxShadow: "0 0 20px 5px rgba(49, 130, 206, 0.3)"
        }}
        height="100%"
        cursor="pointer"
        onClick={handleCardClick}
      >
        <Box position="relative" height={{ base: "150px", sm: "180px", md: "200px" }}>
          <Image src={image} alt={title} objectFit="cover" w="100%" h="100%" />
          {Array.isArray(category) ? (
            <HStack 
              position="absolute"
              top="10px"
              right="10px"
              spacing={2}
            >
              {category.map((cat, index) => (
                <Badge
                  key={index}
                  bg="blackAlpha.600"
                  color="white"
                  px={2}
                  py={1}
                  fontSize={{ base: "xs", md: "sm" }}
                  borderRadius="md"
                  sx={{ backdropFilter: 'blur(4px)' }}
                >
                  {cat}
                </Badge>
              ))}
            </HStack>
          ) : (
            <Badge
              position="absolute"
              top="10px"
              right="10px"
              bg="blackAlpha.600"
              color="white"
              px={2}
              py={1}
              fontSize={{ base: "xs", md: "sm" }}
              borderRadius="md"
              sx={{ backdropFilter: 'blur(4px)' }}
            >
              {category}
            </Badge>
          )}
        </Box>
        <VStack 
          p={{ base: 3, md: 5 }} 
          align="start" 
          spacing={{ base: 2, md: 4 }} 
          height="calc(100% - 200px)" 
          justify="space-between"
        >
          <Box>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="600"
              mb={2}
              color={textColor}
            >
              {title}
            </Text>
            <Text fontSize={{ base: "sm", md: "md" }} color={descriptionColor} noOfLines={3}>
              {excerpt}
            </Text>
          </Box>
          <Box width="100%">
            <Flex 
              justify="space-between" 
              width="100%" 
              mb={4} 
              direction={{ base: "column", sm: "row" }}
              gap={{ base: 2, sm: 0 }}
            >
              <HStack>
                <FaCalendarAlt color="brand.text.muted" size="0.8em" />
                <Text fontSize={{ base: "xs", md: "sm" }} color="brand.text.muted">
                  {date}
                </Text>
              </HStack>
              <HStack>
                <FaClock color="brand.text.muted" size="0.8em" />
                <Text fontSize={{ base: "xs", md: "sm" }} color="brand.text.muted">
                  {readTime} min read
                </Text>
              </HStack>
            </Flex>
            <Button
              colorScheme="blue"
              size={{ base: "sm", md: "md" }}
              width="100%"
              variant="outline"
              onClick={handleButtonClick}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(49, 130, 206, 0.3)",
              }}
              transition="all 0.2s"
            >
              Read More
            </Button>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Animated version for blog page
  return (
    <MotionBox
      bg={bgColor}
      borderRadius="lg"
      overflow="hidden"
      boxShadow="lg"
      backdropFilter="blur(10px)"
      _hover={{ 
        transform: "translateY(-5px)", 
        boxShadow: "0 0 20px 5px rgba(49, 130, 206, 0.3)"
      }}
      height="100%"
      cursor="pointer"
      onClick={handleCardClick}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1, // Stagger based on index
      }}
    >
      <Box position="relative" height={{ base: "150px", sm: "180px", md: "200px" }}>
        <Image src={image} alt={title} objectFit="cover" w="100%" h="100%" />
        {Array.isArray(category) ? (
          <HStack 
            position="absolute"
            top="10px"
            right="10px"
            spacing={2}
          >
            {category.map((cat, index) => (
              <Badge
                key={index}
                bg="blackAlpha.600"
                color="white"
                px={2}
                py={1}
                fontSize={{ base: "xs", md: "sm" }}
                borderRadius="md"
                sx={{ backdropFilter: 'blur(4px)' }}
              >
                {cat}
              </Badge>
            ))}
          </HStack>
        ) : (
          <Badge
            position="absolute"
            top="10px"
            right="10px"
            bg="blackAlpha.600"
            color="white"
            px={2}
            py={1}
            fontSize={{ base: "xs", md: "sm" }}
            borderRadius="md"
            sx={{ backdropFilter: 'blur(4px)' }}
          >
            {category}
          </Badge>
        )}
      </Box>
      <VStack 
        p={{ base: 3, md: 5 }} 
        align="start" 
        spacing={{ base: 2, md: 4 }} 
        height="calc(100% - 200px)" 
        justify="space-between"
      >
        <Box>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="600"
            mb={2}
            color={textColor}
          >
            {title}
          </Text>
          <Text fontSize={{ base: "sm", md: "md" }} color={descriptionColor} noOfLines={3}>
            {excerpt}
          </Text>
        </Box>
        <Box width="100%">
          <Flex 
            justify="space-between" 
            width="100%" 
            mb={4} 
            direction={{ base: "column", sm: "row" }}
            gap={{ base: 2, sm: 0 }}
          >
            <HStack>
              <FaCalendarAlt color="brand.text.muted" size="0.8em" />
              <Text fontSize={{ base: "xs", md: "sm" }} color="brand.text.muted">
                {date}
              </Text>
            </HStack>
            <HStack>
              <FaClock color="brand.text.muted" size="0.8em" />
              <Text fontSize={{ base: "xs", md: "sm" }} color="brand.text.muted">
                {readTime} min read
              </Text>
            </HStack>
          </Flex>
          <Button
            colorScheme="blue"
            size={{ base: "sm", md: "md" }}
            width="100%"
            variant="outline"
            onClick={handleButtonClick}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(49, 130, 206, 0.3)",
            }}
            transition="all 0.2s"
          >
            Read More
          </Button>
        </Box>
      </VStack>
    </MotionBox>
  );
};

export default BlogCard; 